import { useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useTranslation } from 'react-i18next';
import { civilToday, type CivilDate } from '@eis/core';
import { EffectiveDateSelector } from './EffectiveDateSelector';
import {
  anchorDayOf,
  endAfter,
  isOrderedPeriod,
  lastDayOf,
  monthPeriodOf,
  type ValidityPeriod,
} from './validity-period';

export interface ValidityPeriodSelectorProps {
  readonly value: ValidityPeriod;
  /** Called with a COMPLETE value only — a half-typed or reversed period is never emitted. */
  readonly onChange: (value: ValidityPeriod) => void;
  /** The day field's label; *Stichtag* by default. */
  readonly dayLabel?: string;
  readonly dayHelperText?: string;
  readonly locale?: string;
  readonly disabled?: boolean;
}

/**
 * *Stichtag* or *Zeitraum* (SDD-021 §9): the question a list answers in time — who was covered on
 * ONE day, or who was covered at ANY time in a period. A pattern because every temporal list asks
 * it (the group policy's members first; the policy list next).
 *
 * Composed of a small toggle and the sanctioned date pickers (`EffectiveDateSelector`, rule 6):
 * values in and out are civil dates. The period is edited by its first and LAST day — how people
 * say it — and emitted half-open (`to` exclusive), how the API takes it. A period whose end lies
 * before its start is shown as an error on the end field and not emitted, so the list keeps
 * answering the last valid question instead of failing on a half-made one.
 *
 * Switching to *Zeitraum* starts from the calendar month of the day shown (or today); switching
 * back starts from today. The URL half is `useValidityPeriodUrlState`.
 */
export function ValidityPeriodSelector({
  value,
  onChange,
  dayLabel,
  dayHelperText,
  locale = 'de-DE',
  disabled = false,
}: ValidityPeriodSelectorProps): ReactNode {
  const { t } = useTranslation('patterns');
  // The period's fields while they are being edited: emitted only once they make a period.
  const [draft, setDraft] = useState<{ from: CivilDate; last: CivilDate } | null>(null);
  const shown =
    value.kind === 'period' ? { from: value.from, last: lastDayOf(value.to) } : undefined;
  const fields = draft ?? shown;
  const reversed = fields !== undefined && !isOrderedPeriod(fields.from, fields.last);

  const edit = (next: { from: CivilDate; last: CivilDate }): void => {
    if (isOrderedPeriod(next.from, next.last)) {
      setDraft(null);
      onChange({ kind: 'period', from: next.from, to: endAfter(next.last) });
      return;
    }
    setDraft(next);
  };

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: 'flex-start' }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={value.kind}
        disabled={disabled}
        aria-label={t('validityPeriod.mode')}
        onChange={(_event, kind: ValidityPeriod['kind'] | null) => {
          if (kind === null || kind === value.kind) return;
          setDraft(null);
          onChange(
            kind === 'period'
              ? { kind: 'period', ...monthPeriodOf(anchorDayOf(value)) }
              : { kind: 'day' },
          );
        }}
        sx={{ flexShrink: 0 }}
      >
        <ToggleButton value="day">{t('validityPeriod.day')}</ToggleButton>
        <ToggleButton value="period">{t('validityPeriod.period')}</ToggleButton>
      </ToggleButtonGroup>

      {value.kind === 'day' || fields === undefined ? (
        <Box sx={{ width: { xs: '100%', sm: 220 } }}>
          <EffectiveDateSelector
            label={dayLabel ?? t('validityPeriod.day')}
            helperText={dayHelperText}
            value={value.kind === 'day' ? (value.validAt ?? civilToday()) : null}
            disabled={disabled}
            locale={locale}
            onChange={(next) => {
              // Cleared means "today" — a day list cannot answer "no day".
              onChange(next === null ? { kind: 'day' } : { kind: 'day', validAt: next });
            }}
          />
        </Box>
      ) : (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <Box sx={{ width: { xs: '100%', sm: 200 } }}>
            <EffectiveDateSelector
              label={t('validityPeriod.from')}
              value={fields.from}
              disabled={disabled}
              locale={locale}
              onChange={(next) => {
                if (next !== null) edit({ from: next, last: fields.last });
              }}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', sm: 200 } }}>
            <EffectiveDateSelector
              label={t('validityPeriod.to')}
              value={fields.last}
              // A reversed period is the end field's error: "not before the start".
              minDate={reversed ? fields.from : undefined}
              helperText={t('validityPeriod.toHelp')}
              disabled={disabled}
              locale={locale}
              onChange={(next) => {
                if (next !== null) edit({ from: fields.from, last: next });
              }}
            />
          </Box>
        </Stack>
      )}
    </Stack>
  );
}
